import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useAuth } from '../../auth/AuthProvider';
import { hasPermission, PERMISSIONS } from '../../auth/permissions';
import { styles, colors, radius, shadows } from '../../styles/theme';
import useCachedQuery from '../../hooks/useCachedQuery';
import useDebouncedValue from '../../hooks/useDebouncedValue';
import useUrlState from '../../hooks/useUrlState';
import { searchMatchPre } from '../../utils/searchMatch';
import { logError } from '../../utils/logError';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001';

const getAuthHeaders = () => {
  const token = localStorage.getItem('access_token');
  return token ? { 'Authorization': `Bearer ${token}` } : {};
};

const TransferFiyatPage = () => {
  const { user } = useAuth();
  const canManageTransfer = user && hasPermission(user.role, PERMISSIONS.MANAGE_TRANSFER_PRICES, user.effectivePermissions);
  const userCacheKey = user?.id || user?.email || user?.name || 'anon';
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [bulkLoading, setBulkLoading] = useState(false);
  // Görünüm/arama/tip filtreleri URL'e bağlı: detaya gidip geri dönünce / yenileyince korunur.
  const [viewMode, setViewMode] = useUrlState('view', 'karlilik');
  const [arama, setArama] = useUrlState('arama', '', { debounceMs: 300 });
  const [saveMsg, setSaveMsg] = useState(null);
  const debouncedArama = useDebouncedValue(arama, 300);
  const [tipFilter, setTipFilter] = useUrlState('tip', 'all');
  const [defaultMargin, setDefaultMargin] = useState(35);
  const [editingDefault, setEditingDefault] = useState(false);
  const [editingValue, setEditingValue] = useState('');
  const [defaultSaving, setDefaultSaving] = useState(false);
  const [applyingProducedMargin, setApplyingProducedMargin] = useState(false);

  // "+ Yeni Ürün Ekle" modal state
  const [showAddModal, setShowAddModal] = useState(false);
  const [malzemeler, setMalzemeler] = useState([]);
  const [malzemeSearch, setMalzemeSearch] = useState('');
  const [selectedMalzemeId, setSelectedMalzemeId] = useState('');
  const [uretimMaliyeti, setUretimMaliyeti] = useState('');
  const [karMarjiPct, setKarMarjiPct] = useState('');
  const [manuelFiyat, setManuelFiyat] = useState('');
  const [addLoading, setAddLoading] = useState(false);

  useEffect(() => {
    fetch(`${API_URL}/api/system/transfer-default-margin`, { headers: getAuthHeaders() })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d && d.value != null) setDefaultMargin(d.value); })
      .catch(() => { /* silent fail; widget keeps default */ });
  }, []);

  // Malzeme listesi (modal için)
  useEffect(() => {
    fetch(`${API_URL}/api/malzemeler`, { headers: getAuthHeaders() })
      .then(r => r.ok ? r.json() : [])
      .then(data => {
        setMalzemeler(Array.isArray(data) ? data : (data.items || []));
      })
      .catch(() => { /* silent fail */ });
  }, []);

  const handleSaveDefault = async () => {
    const parsed = parseFloat(editingValue);
    if (!Number.isFinite(parsed) || parsed < 0) {
      setSaveMsg('Geçersiz değer');
      setTimeout(() => setSaveMsg(null), 2000);
      return;
    }
    setDefaultSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/system/transfer-default-margin`, {
        method: 'PUT',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ value: parsed })
      });
      if (!res.ok) {
        if (res.status === 403) {
          setSaveMsg('Yetki yetersiz');
        } else {
          setSaveMsg('Hata!');
        }
        setTimeout(() => setSaveMsg(null), 2500);
      } else {
        const data = await res.json().catch(() => ({}));
        setDefaultMargin(data.value != null ? data.value : parsed);
        setEditingDefault(false);
        setSaveMsg('Default kâr marjı kaydedildi');
        setTimeout(() => setSaveMsg(null), 2000);
      }
    } catch (err) {
      setSaveMsg('Hata!');
      setTimeout(() => setSaveMsg(null), 2500);
      logError('Default margin kaydetme hatası:', err);
    } finally {
      setDefaultSaving(false);
    }
  };

  const handleApplyProducedMargin = async () => {
    const margin = Number(defaultMargin);
    if (!Number.isFinite(margin) || margin < 0) {
      setSaveMsg('Geçersiz kâr marjı');
      setTimeout(() => setSaveMsg(null), 2500);
      return;
    }

    const produced = fiyatlarRaw.filter(
      f => f.tip === 'YARI_MAMUL' && f.transfer_edilir !== false
    );
    if (produced.length === 0) {
      setSaveMsg('Uygulanacak üretilen ürün yok');
      setTimeout(() => setSaveMsg(null), 2500);
      return;
    }

    const ok = window.confirm(
      `%${margin} kâr marjı ${produced.length} üretilen ürüne uygulanacak. ` +
      'Bu işlem mevcut manuel fiyatları temizleyip fiyatları maliyet + marj ile yeniden doldurur. Devam edilsin mi?'
    );
    if (!ok) return;

    setApplyingProducedMargin(true);
    try {
      for (const item of produced) {
        const res = await fetch(`${API_URL}/api/transfer-prices`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
          body: JSON.stringify({
            malzeme_id: item.malzeme_id,
            uretim_maliyeti: item.uretim_maliyeti || 0,
            kar_marji_pct: margin,
            manuel_fiyat: null
          })
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
      }
      setSaveMsg(`${produced.length} üretilen ürünün fiyatı dolduruldu`);
      setTimeout(() => setSaveMsg(null), 3000);
      await revalidate({ force: true });
    } catch (err) {
      logError('Üretilen ürünlere marj uygulama hatası:', err);
      setSaveMsg('Hata!');
      setTimeout(() => setSaveMsg(null), 3000);
    } finally {
      setApplyingProducedMargin(false);
    }
  };

  const fetchFiyatlar = useCallback(async () => {
    const res = await fetch(`${API_URL}/api/transfer-prices`, { headers: getAuthHeaders() });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = await res.json();
    return Array.isArray(data) ? data : [];
  }, []);

  const {
    data: fiyatlarRaw = [],
    isLoading: loading,
    isValidating,
    mutate,
    revalidate
  } = useCachedQuery(['transfer-prices', userCacheKey], fetchFiyatlar, { staleTime: 10000 });

  const fiyatlar = useMemo(() => {
    const search = debouncedArama ? debouncedArama.toLowerCase().trim() : '';
    return fiyatlarRaw.filter(f => {
      if (tipFilter !== 'all' && f.tip !== tipFilter) return false;
      if (!search) return true;
      return searchMatchPre(f.malzeme_ad, search) ||
             searchMatchPre(f.malzeme_kod, search);
    });
  }, [debouncedArama, fiyatlarRaw, tipFilter]);

  const handleEdit = (item) => {
    if (!canManageTransfer) return;
    setEditingId(item.malzeme_id);
    setEditForm({
      uretim_maliyeti: item.uretim_maliyeti,
      kar_marji_pct: item.kar_marji_pct,
      manuel_fiyat: item.manuel_fiyat || item.efektif_fiyat || item.transfer_fiyati || ''
    });
  };

  const handleRowClick = (item) => {
    if (!canManageTransfer) return;
    if (editingId === item.malzeme_id) return;
    handleEdit(item);
  };

  const buildOptimisticRow = (current, form) => {
    const uretimMaliyeti = parseFloat(form.uretim_maliyeti);
    const karMarjiPct = parseFloat(form.kar_marji_pct);
    const transferFiyati = (Number.isFinite(uretimMaliyeti) && Number.isFinite(karMarjiPct))
      ? (uretimMaliyeti * (1 + karMarjiPct / 100))
      : current.transfer_fiyati;
    const manuelFiyat = form.manuel_fiyat !== '' && form.manuel_fiyat != null
      ? parseFloat(form.manuel_fiyat)
      : null;
    const efektifFiyat = manuelFiyat || transferFiyati;
    const posSatisFiyati = current.pos_satis_fiyati || null;

    const dukkanKar = (posSatisFiyati && efektifFiyat) ? (posSatisFiyati - efektifFiyat) : null;
    const dukkanKarPct = (posSatisFiyati && efektifFiyat)
      ? ((posSatisFiyati - efektifFiyat) / posSatisFiyati * 100)
      : null;
    const foodCostPct = (posSatisFiyati && efektifFiyat)
      ? (efektifFiyat / posSatisFiyati * 100)
      : null;

    return {
      ...current,
      uretim_maliyeti: Number.isFinite(uretimMaliyeti) ? uretimMaliyeti : current.uretim_maliyeti,
      kar_marji_pct: Number.isFinite(karMarjiPct) ? karMarjiPct : current.kar_marji_pct,
      transfer_fiyati: Number.isFinite(transferFiyati) ? transferFiyati : current.transfer_fiyati,
      manuel_fiyat: manuelFiyat,
      efektif_fiyat: Number.isFinite(efektifFiyat) ? efektifFiyat : current.efektif_fiyat,
      kar_tutari: Number.isFinite((efektifFiyat || 0) - (uretimMaliyeti || 0))
        ? ((efektifFiyat || 0) - (uretimMaliyeti || 0))
        : current.kar_tutari,
      dukkan_kar: dukkanKar,
      dukkan_kar_pct: dukkanKarPct,
      food_cost_pct: foodCostPct
    };
  };

  const handleSave = async (malzemeId) => {
    const current = fiyatlarRaw.find(f => f.malzeme_id === malzemeId);
    if (!current) return;

    const previousData = mutate(prev => {
      const list = Array.isArray(prev) ? prev : [];
      return list.map(item => {
        if (item.malzeme_id !== malzemeId) return item;
        return buildOptimisticRow(item, editForm);
      });
    }, { revalidate: false });

    try {
      const res = await fetch(`${API_URL}/api/transfer-prices`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({
          malzeme_id: malzemeId,
          uretim_maliyeti: 0,
          kar_marji_pct: parseFloat(editForm.kar_marji_pct),
          manuel_fiyat: editForm.manuel_fiyat ? parseFloat(editForm.manuel_fiyat) : null
        })
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setEditingId(null);
      setSaveMsg('Kaydedildi');
      setTimeout(() => setSaveMsg(null), 2000);
      await revalidate({ force: true });
    } catch (err) {
      mutate(previousData, { revalidate: false });
      setSaveMsg('Hata!');
      setTimeout(() => setSaveMsg(null), 3000);
      logError('Kaydetme hatası:', err);
    }
  };

  const handleBulkUpdate = async () => {
    setBulkLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/transfer-prices/bulk-update`, { method: 'POST', headers: getAuthHeaders() });
      const data = await res.json();
      setSaveMsg(`${data.updated || 0} ürün güncellendi`);
      setTimeout(() => setSaveMsg(null), 3000);
      await revalidate({ force: true });
    } catch (err) {
      logError('Toplu güncelleme hatası:', err);
    }
    setBulkLoading(false);
  };

  // Modal: Mevcut Transfer Fiyatları'nda olmayan + aktif malzemeler
  const filteredMalzemeler = useMemo(() => {
    const existingIds = new Set(fiyatlarRaw.map(f => f.malzeme_id));
    const search = (malzemeSearch || '').toLowerCase().trim();
    return malzemeler.filter(m =>
      m.aktif &&
      !existingIds.has(m.id) &&
      (!search ||
        searchMatchPre(m.kod, search) ||
        searchMatchPre(m.ad, search))
    );
  }, [malzemeler, fiyatlarRaw, malzemeSearch]);

  const resetAddForm = () => {
    setSelectedMalzemeId('');
    setUretimMaliyeti('');
    setKarMarjiPct('');
    setManuelFiyat('');
    setMalzemeSearch('');
  };

  const handleSelectMalzeme = (m) => {
    setSelectedMalzemeId(m.id);
    // Auto-fill: malzemenin son alış fiyatı
    if (m.fiyat != null && m.fiyat !== '') {
      setUretimMaliyeti(String(m.fiyat));
    }
  };

  const handleAddProduct = async () => {
    if (!selectedMalzemeId) {
      alert('Lütfen bir malzeme seçin');
      return;
    }
    setAddLoading(true);
    try {
      const body = {
        malzeme_id: parseInt(selectedMalzemeId, 10),
        uretim_maliyeti: uretimMaliyeti ? parseFloat(uretimMaliyeti) : 0,
        kar_marji_pct: parseFloat(karMarjiPct) || defaultMargin,
      };
      if (manuelFiyat) body.manuel_fiyat = parseFloat(manuelFiyat);

      const res = await fetch(`${API_URL}/api/transfer-prices`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });

      if (res.ok) {
        setShowAddModal(false);
        resetAddForm();
        setSaveMsg('Ürün eklendi');
        setTimeout(() => setSaveMsg(null), 2500);
        await revalidate({ force: true });
      } else {
        let errMsg = 'Ekleme başarısız';
        try {
          const errBody = await res.json();
          errMsg = errBody.detail || errMsg;
        } catch (_) { /* ignore */ }
        alert(`Hata: ${errMsg}`);
      }
    } catch (err) {
      logError('Ürün ekleme hatası:', err);
      alert('Hata: Ekleme başarısız');
    } finally {
      setAddLoading(false);
    }
  };

  const closeAddModal = () => {
    setShowAddModal(false);
    resetAddForm();
  };

  const previewFiyat = editForm.uretim_maliyeti && editForm.kar_marji_pct
    ? (parseFloat(editForm.uretim_maliyeti) * (1 + parseFloat(editForm.kar_marji_pct) / 100)).toFixed(2)
    : '-';

  const toplamUrun = fiyatlar.length;
  const ortMaliyet = fiyatlar.length > 0
    ? (fiyatlar.reduce((sum, f) => sum + (f.uretim_maliyeti || 0), 0) / fiyatlar.length).toFixed(2)
    : 0;
  // Yerinde üretim satırları KPI ortalamalarına girmez — satırda da '–' gösteriliyor
  const withSatis = fiyatlar.filter(f => f.pos_satis_fiyati > 0 && f.transfer_edilir !== false);
  const ortKarlilik = withSatis.length > 0
    ? (withSatis.reduce((sum, f) => sum + (f.dukkan_kar_pct || 0), 0) / withSatis.length).toFixed(1)
    : 0;
  const toplamKar = withSatis.reduce((sum, f) => sum + (f.dukkan_kar || 0), 0).toFixed(2);
  const ortFoodCost = withSatis.length > 0
    ? (withSatis.reduce((sum, f) => sum + (f.food_cost_pct || 0), 0) / withSatis.length).toFixed(1)
    : 0;

  const getKarColor = (pct) => {
    if (!pct || pct <= 0) return { bg: '#f8d7da', color: '#721c24' };
    if (pct < 30) return { bg: '#fff3cd', color: '#856404' };
    if (pct < 50) return { bg: '#d4edda', color: '#155724' };
    return { bg: '#c8e6c9', color: '#1b5e20' };
  };

  const getFoodCostColor = (pct) => {
    if (!pct) return { bg: '#e9ecef', color: '#495057' };
    if (pct < 28) return { bg: '#d4edda', color: '#155724' };
    if (pct <= 35) return { bg: '#fff3cd', color: '#856404' };
    if (pct <= 50) return { bg: '#ffe0b2', color: '#e65100' };
    return { bg: '#f8d7da', color: '#721c24' };
  };

  const editableCellStyle = {
    padding: '8px',
    textAlign: 'right',
    cursor: canManageTransfer ? 'pointer' : 'default',
    position: 'relative'
  };

  const editableValueStyle = (hasValue) => ({
    fontFamily: 'monospace',
    fontWeight: hasValue ? 'bold' : 'normal',
    borderBottom: canManageTransfer ? '1px dashed #90caf9' : 'none',
    paddingBottom: '1px',
    color: hasValue ? undefined : '#ccc'
  });

  const inputStyle = (borderColor) => ({
    width: '100px', padding: '6px 8px', textAlign: 'right',
    border: `2px solid ${borderColor || '#1565c0'}`, borderRadius: radius.sm,
    fontSize: '13px', fontWeight: 'bold'
  });

  const renderTipBadge = (tip) => {
    if (tip === 'YARI_MAMUL') {
      return (
        <span style={{
          background: '#e3f2fd', color: '#1565c0', padding: '2px 8px',
          borderRadius: radius.lg, fontSize: '10px', fontWeight: 'bold',
          whiteSpace: 'nowrap'
        }}>
          🏭 Yarı Mamul
        </span>
      );
    }
    return (
      <span style={{
        background: '#fff3e0', color: '#e65100', padding: '2px 8px',
        borderRadius: radius.lg, fontSize: '10px', fontWeight: 'bold',
        whiteSpace: 'nowrap'
      }}>
        📦 Ham Madde
      </span>
    );
  };

  // Yerinde üretim rozeti: transfer_edilir=false olan yarı mamuller markada üretilir,
  // maliyette transfer fiyatı değil üretim maliyeti kullanılır.
  const renderYerindeBadge = (f) => {
    if (f.transfer_edilir !== false) return null;
    return (
      <span style={{
        marginLeft: 6, padding: '2px 8px', borderRadius: 10,
        fontSize: '11px', background: '#fff3e0', color: '#e65100',
        whiteSpace: 'nowrap'
      }} title="Bu ürün kullanıldığı markada üretilir; maliyette transfer fiyatı DEĞİL üretim maliyeti kullanılır">
        🏠 Yerinde Üretim
      </span>
    );
  };

  return (
    <div>
      {/* Header */}
      <div style={{
        background: `linear-gradient(135deg, ${styles.primary}, ${styles.primaryDark})`,
        color: 'white', padding: '15px 25px',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center'
      }}>
        <div>
          <h1 style={{ margin: 0, fontSize: '20px' }}>Transfer Fiyatları & Karlılık</h1>
          <div style={{ fontSize: '12px', opacity: 0.8, marginTop: '4px' }}>
            {toplamUrun} ürün | {withSatis.length} satış fiyatlı
          </div>
        </div>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          {saveMsg && (
            <span style={{
              background: saveMsg.includes('Hata') ? '#f8d7da' : '#d4edda',
              color: saveMsg.includes('Hata') ? '#721c24' : '#155724',
              padding: '6px 14px', borderRadius: radius.sm, fontSize: '12px', fontWeight: 'bold'
            }}>{saveMsg}</span>
          )}
          <div style={{ display: 'flex', background: 'rgba(255,255,255,0.15)', borderRadius: radius.sm, overflow: 'hidden' }}>
            <button onClick={() => setViewMode('karlilik')}
              style={{
                padding: '6px 14px', border: 'none', fontSize: '12px', cursor: 'pointer',
                background: viewMode === 'karlilik' ? 'white' : 'transparent',
                color: viewMode === 'karlilik' ? styles.primary : 'white', fontWeight: 'bold'
              }}>
              Karlılık
            </button>
            <button onClick={() => setViewMode('transfer')}
              style={{
                padding: '6px 14px', border: 'none', fontSize: '12px', cursor: 'pointer',
                background: viewMode === 'transfer' ? 'white' : 'transparent',
                color: viewMode === 'transfer' ? styles.primary : 'white', fontWeight: 'bold'
              }}>
              Transfer
            </button>
          </div>
          {canManageTransfer && <button onClick={() => { resetAddForm(); setKarMarjiPct(String(defaultMargin)); setShowAddModal(true); }}
            title="Transfer Fiyatları listesine yeni bir ürün ekle"
            style={{
              background: '#2e7d32', color: 'white', border: 'none',
              padding: '8px 18px', borderRadius: radius.sm, fontWeight: 'bold', cursor: 'pointer'
            }}>
            + Yeni Ürün
          </button>}
          {canManageTransfer && <button onClick={handleBulkUpdate} disabled={bulkLoading}
            title="Yarı mamul reçete maliyetlerini ve ham madde alış fiyatlarını sync eder. Manuel girdiğiniz değerlere dokunmaz."
            style={{
              background: colors.surface, color: styles.primary, border: 'none',
              padding: '8px 20px', borderRadius: radius.sm, fontWeight: 'bold', cursor: bulkLoading ? 'wait' : 'pointer',
              opacity: bulkLoading ? 0.6 : 1
            }}>
            {bulkLoading ? 'Güncelleniyor...' : 'Toplu Güncelle'}
          </button>}
        </div>
      </div>

      {/* Default Kâr Marjı Widget */}
      <div style={{ padding: '15px 20px 0' }}>
        <div style={{
          background: colors.surface,
          padding: '12px 20px',
          borderRadius: radius.md,
          border: `1px solid ${colors.border}`,
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          flexWrap: 'wrap'
        }}>
          <span style={{ fontSize: '13px', color: colors.textSecondary }}>Varsayılan Kâr Marjı:</span>
          {!editingDefault ? (
            <>
              <span style={{ fontSize: '20px', fontWeight: 'bold', color: styles.primary }}>%{defaultMargin}</span>
              {canManageTransfer && (
                <button
                  onClick={() => { setEditingValue(String(defaultMargin)); setEditingDefault(true); }}
                  style={{
                    background: '#1565c0', color: 'white', border: 'none', borderRadius: radius.sm,
                    padding: '5px 12px', cursor: 'pointer', fontSize: '12px', fontWeight: 'bold'
                  }}>
                  Düzenle
                </button>
              )}
            </>
          ) : (
            <>
              <input
                type="number"
                step="0.5"
                value={editingValue}
                onChange={e => setEditingValue(e.target.value)}
                autoFocus
                style={{
                  width: '90px', padding: '6px 8px', textAlign: 'right',
                  border: `2px solid #1565c0`, borderRadius: radius.sm,
                  fontSize: '14px', fontWeight: 'bold'
                }}
              />
              <button
                onClick={handleSaveDefault}
                disabled={defaultSaving}
                style={{
                  background: '#2e7d32', color: 'white', border: 'none', borderRadius: radius.sm,
                  padding: '6px 14px', cursor: defaultSaving ? 'wait' : 'pointer',
                  fontSize: '12px', fontWeight: 'bold', opacity: defaultSaving ? 0.6 : 1
                }}>
                {defaultSaving ? 'Kaydediliyor...' : 'Kaydet'}
              </button>
              <button
                onClick={() => setEditingDefault(false)}
                disabled={defaultSaving}
                style={{
                  background: '#e0e0e0', color: '#333', border: 'none', borderRadius: radius.sm,
                  padding: '6px 12px', cursor: 'pointer', fontSize: '12px'
                }}>
                İptal
              </button>
            </>
          )}
          {canManageTransfer && (
            <button
              onClick={handleApplyProducedMargin}
              disabled={applyingProducedMargin}
              title="Sadece kendi ürettiğimiz ve transfer edilen yarı mamullerin fiyatlarını maliyet + bu marj ile doldurur"
              style={{
                background: '#e65100', color: 'white', border: 'none', borderRadius: radius.sm,
                padding: '6px 14px', cursor: applyingProducedMargin ? 'wait' : 'pointer',
                fontSize: '12px', fontWeight: 'bold', opacity: applyingProducedMargin ? 0.6 : 1
              }}>
              {applyingProducedMargin ? 'Uygulanıyor...' : 'Üretilen Ürünlere Uygula'}
            </button>
          )}
          <span style={{ fontSize: '11px', color: colors.textTertiary, marginLeft: 'auto' }}>
            Önce marjı kaydedin, sonra üretilen ürünlere uygulayın. Sonrasında her ürünün Transfer Fiyatı tek tek değiştirilebilir.
          </span>
        </div>
      </div>

      {/* İstatistik Kartları */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px', padding: '15px 20px' }}>
        <div style={{ background: '#e3f2fd', padding: '15px', borderRadius: radius.md, textAlign: 'center' }}>
          <div style={{ fontSize: '22px', fontWeight: 'bold', color: '#1565c0' }}>{toplamUrun}</div>
          <div style={{ fontSize: '11px', color: '#1565c0', marginTop: '4px' }}>Toplam Ürün</div>
        </div>
        <div style={{ background: '#e8f5e9', padding: '15px', borderRadius: radius.md, textAlign: 'center' }}>
          <div style={{ fontSize: '22px', fontWeight: 'bold', color: '#2e7d32' }}>{ortMaliyet}</div>
          <div style={{ fontSize: '11px', color: '#2e7d32', marginTop: '4px' }}>Ort. Maliyet</div>
        </div>
        <div style={{
          background: getKarColor(parseFloat(ortKarlilik)).bg, padding: '15px', borderRadius: radius.md, textAlign: 'center'
        }}>
          <div style={{ fontSize: '22px', fontWeight: 'bold', color: getKarColor(parseFloat(ortKarlilik)).color }}>
            %{ortKarlilik}
          </div>
          <div style={{ fontSize: '11px', color: getKarColor(parseFloat(ortKarlilik)).color, marginTop: '4px' }}>Ort. Karlılık</div>
        </div>
        <div style={{
          background: getFoodCostColor(parseFloat(ortFoodCost)).bg, padding: '15px', borderRadius: radius.md, textAlign: 'center'
        }}>
          <div style={{ fontSize: '22px', fontWeight: 'bold', color: getFoodCostColor(parseFloat(ortFoodCost)).color }}>
            %{ortFoodCost}
          </div>
          <div style={{ fontSize: '11px', color: getFoodCostColor(parseFloat(ortFoodCost)).color, marginTop: '4px' }}>Ort. Food Cost</div>
        </div>
        <div style={{ background: '#f3e5f5', padding: '15px', borderRadius: radius.md, textAlign: 'center' }}>
          <div style={{ fontSize: '22px', fontWeight: 'bold', color: '#6a1b9a' }}>{toplamKar}</div>
          <div style={{ fontSize: '11px', color: '#6a1b9a', marginTop: '4px' }}>Top. Birim Kâr</div>
        </div>
      </div>

      {/* Bilgi banner */}
      <div style={{ padding: '0 20px' }}>
        <div style={{ background: '#e3f2fd', padding: '10px 15px', borderRadius: radius.sm, marginBottom: '10px', fontSize: '12px', color: '#1565c0' }}>
          <strong>Formül:</strong>{' '}
          Food Cost = Transfer Fiyatı / Satış Fiyatı x 100 |{' '}
          Karlılık = (Satış - Transfer) / Satış x 100 |{' '}
          Kâr = Satış Fiyatı - Transfer Fiyatı
        </div>
        {canManageTransfer && (
          <div style={{
            background: '#fff8e1', padding: '8px 15px', borderRadius: radius.sm, marginBottom: '10px',
            fontSize: '12px', color: '#f57f17', display: 'flex', alignItems: 'center', gap: '6px'
          }}>
            <span style={{ fontSize: '16px' }}>&#9998;</span>
            <span>Satıra tıklayarak fiyatları düzenleyebilirsiniz. Düzenleme sonrası <strong>Kaydet</strong> butonuna basın.</span>
          </div>
        )}
      </div>

      <div style={{ padding: '0 20px 12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
          <input
            placeholder="Ürün ara (kod veya ad)..."
            value={arama}
            onChange={e => setArama(e.target.value)}
            style={{ width: '320px', maxWidth: '100%', padding: '8px 12px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, fontSize: '13px' }}
          />
          <div style={{ display: 'flex', gap: '4px', background: colors.backgroundAlt, padding: '4px', borderRadius: radius.sm }}>
            {[
              { key: 'all', label: 'Tümü', title: 'Tüm ürünler' },
              { key: 'YARI_MAMUL', label: '🏭 Yarı Mamul', title: 'Sadece yarı mamuller' },
              { key: 'HAM_MADDE', label: '📦 Ham Madde', title: 'Sadece ham maddeler' }
            ].map(opt => {
              const active = tipFilter === opt.key;
              return (
                <button
                  key={opt.key}
                  type="button"
                  onClick={() => setTipFilter(opt.key)}
                  title={opt.title}
                  style={{
                    background: active ? colors.surface : 'transparent',
                    color: active ? styles.primary : colors.textSecondary,
                    border: 'none',
                    borderRadius: radius.sm,
                    padding: '5px 10px',
                    fontSize: '12px',
                    fontWeight: active ? 'bold' : 'normal',
                    cursor: 'pointer',
                    boxShadow: active ? '0 1px 2px rgba(0,0,0,0.08)' : 'none',
                    opacity: active ? 1 : 0.7
                  }}>
                  {opt.label}
                </button>
              );
            })}
          </div>
        </div>
        {isValidating && !loading && (
          <span style={{ fontSize: '12px', color: colors.textSecondary, background: '#f1f3f5', borderRadius: radius.lg, padding: '4px 10px' }}>
            Güncelleniyor...
          </span>
        )}
      </div>

      <div style={{ padding: '0 20px 20px' }}>
        {loading ? (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px', background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: radius.md }}>
            <tbody>
              {Array.from({ length: 8 }).map((_, rowIdx) => (
                <tr key={`tf-skel-${rowIdx}`} style={{ borderBottom: `1px solid ${colors.border}` }}>
                  {Array.from({ length: viewMode === 'karlilik' ? 9 : 10 }).map((__, colIdx) => (
                    <td key={`tf-skel-${rowIdx}-${colIdx}`} style={{ padding: '10px' }}>
                      <div style={{ height: '12px', borderRadius: radius.sm, background: '#e9ecef', width: colIdx === 0 ? '75%' : '60%' }} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        ) : fiyatlar.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#888' }}>
            <div style={{ fontSize: '16px', marginBottom: '8px' }}>Sonuç bulunamadı</div>
            {canManageTransfer && (
              <div style={{ fontSize: '13px' }}>
                "Toplu Güncelle" butonuna tıklayarak reçetelerden transfer fiyatlarını oluşturabilirsiniz.
              </div>
            )}
          </div>
        ) : viewMode === 'karlilik' ? (
          /* ===== KARLILIK GÖRÜNÜMÜ ===== */
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px', background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: radius.md }}>
            <thead>
              <tr style={{ background: colors.backgroundAlt, borderBottom: '2px solid #dee2e6' }}>
                <th style={{ padding: '7px', textAlign: 'left' }}>Ürün Adı</th>
                <th style={{ padding: '7px', textAlign: 'center', maxWidth: '100px' }}>Tip</th>
                <th style={{ padding: '7px', textAlign: 'right' }}>Üretim Maliyeti</th>
                <th style={{ padding: '7px', textAlign: 'right' }}>Transfer Fiyatı</th>
                <th style={{ padding: '7px', textAlign: 'right' }}>Satış Fiyatı</th>
                <th style={{ padding: '7px', textAlign: 'right' }}>Dükkan Kârı</th>
                <th style={{ padding: '7px', textAlign: 'center' }}>Karlılık %</th>
                <th style={{ padding: '7px', textAlign: 'center' }}>Food Cost %</th>
                <th style={{ padding: '7px', textAlign: 'center', width: '120px' }}>İşlem</th>
              </tr>
            </thead>
            <tbody>
              {[...fiyatlar].sort((a, b) => (b.dukkan_kar_pct || 0) - (a.dukkan_kar_pct || 0)).map(f => {
                const isEditing = editingId === f.malzeme_id;
                const karColor = getKarColor(f.dukkan_kar_pct);
                const fcColor = getFoodCostColor(f.food_cost_pct);
                const efektifFiyat = f.efektif_fiyat || f.transfer_fiyati;
                const posPrice = f.pos_satis_fiyati;
                const posFoodCost = f.pos_food_cost_pct;
                const posLoss = posPrice && efektifFiyat ? posPrice < efektifFiyat : false;

                let editKar = null, editKarPct = null, editFoodCost = null;
                if (isEditing) {
                  const manuelFiyat = editForm.manuel_fiyat ? parseFloat(editForm.manuel_fiyat) : null;
                  const um = parseFloat(editForm.uretim_maliyeti) || 0;
                  const marj = parseFloat(editForm.kar_marji_pct) || 0;
                  const hesaplanan = um * (1 + marj / 100);
                  const editEfektifFiyat = manuelFiyat || hesaplanan;
                  const sf = posPrice || 0;
                  if (sf > 0 && editEfektifFiyat > 0) {
                    editKar = (sf - editEfektifFiyat).toFixed(2);
                    editKarPct = ((sf - editEfektifFiyat) / sf * 100).toFixed(1);
                    editFoodCost = (editEfektifFiyat / sf * 100).toFixed(1);
                  }
                }

                return (
                  <tr key={f.malzeme_id}
                      onClick={() => !isEditing && handleRowClick(f)}
                      style={{
                        borderBottom: `1px solid ${colors.border}`,
                        background: isEditing ? '#fff9c4' : 'transparent',
                        cursor: canManageTransfer && !isEditing ? 'pointer' : 'default',
                        transition: 'background 0.15s'
                      }}
                      onMouseEnter={e => { if (!isEditing && canManageTransfer) e.currentTarget.style.background = '#e3f2fd'; else if (!isEditing) e.currentTarget.style.background = '#f8f9fa'; }}
                      onMouseLeave={e => { if (!isEditing) e.currentTarget.style.background = 'transparent'; }}>
                    <td style={{ padding: '8px', fontWeight: 'bold' }}>
                      <div>{f.malzeme_ad}</div>
                      <div style={{ fontSize: '10px', color: '#888', fontFamily: 'monospace' }}>{f.malzeme_kod}</div>
                    </td>
                    <td style={{ padding: '8px', textAlign: 'center', maxWidth: '100px' }}>
                      {renderTipBadge(f.tip)}
                      {renderYerindeBadge(f)}
                    </td>
                    <td style={{ padding: '8px', textAlign: 'right' }}>
                      <span style={{ fontFamily: 'monospace', color: '#888' }}>{(f.uretim_maliyeti || 0).toFixed(2)}</span>
                    </td>
                    {/* Transfer Fiyatı — düzenlenebilir (manuel_fiyat olarak kaydedilir) */}
                    <td style={editableCellStyle} onClick={e => { if (isEditing) e.stopPropagation(); }}>
                      {isEditing ? (
                        <input type="number" step="0.01" value={editForm.manuel_fiyat}
                          onChange={e => setEditForm(p => ({ ...p, manuel_fiyat: e.target.value }))}
                          style={inputStyle('#1565c0')} />
                      ) : f.transfer_edilir === false ? (
                        <span style={{ color: '#bbb' }}>–</span>
                      ) : (
                        <span style={editableValueStyle(true)}>
                          {f.efektif_fiyat != null
                            ? f.efektif_fiyat.toFixed(2)
                            : (f.transfer_fiyati != null ? f.transfer_fiyati.toFixed(2) : '0.00')}
                        </span>
                      )}
                    </td>
                    {/* Satış Fiyatı — POS-tabanlı, read-only */}
                    <td style={{ padding: '8px', textAlign: 'right' }}>
                      {posPrice ? (
                        <div>
                          <span style={{
                            fontFamily: 'monospace',
                            fontWeight: 'bold',
                            color: posLoss ? '#c62828' : undefined
                          }}>
                            {posPrice.toFixed(2)}
                          </span>
                          <span style={{
                            marginLeft: '6px', fontSize: '9px', padding: '1px 6px',
                            borderRadius: '8px', background: '#e3f2fd', color: '#1565c0'
                          }}>POS</span>
                        </div>
                      ) : <span style={{ color: '#ccc' }}>-</span>}
                    </td>
                    <td style={{ padding: '8px', textAlign: 'right' }}>
                      {isEditing ? (
                        <span style={{ fontFamily: 'monospace', fontWeight: 'bold', color: editKar > 0 ? '#2e7d32' : '#c62828' }}>
                          {editKar ? `${editKar}` : '-'}
                        </span>
                      ) : f.transfer_edilir === false ? (
                        <span style={{ color: '#bbb' }}>–</span>
                      ) : (
                        <span style={{
                          fontFamily: 'monospace', fontWeight: 'bold',
                          color: f.dukkan_kar > 0 ? '#2e7d32' : f.dukkan_kar < 0 ? '#c62828' : '#888'
                        }}>
                          {f.dukkan_kar != null ? `${f.dukkan_kar.toFixed(2)}` : '-'}
                        </span>
                      )}
                    </td>
                    <td style={{ padding: '8px', textAlign: 'center' }}>
                      {isEditing ? (
                        editKarPct ? (
                          <span style={{
                            display: 'inline-block', padding: '3px 10px', borderRadius: radius.lg, fontWeight: 'bold', fontSize: '11px',
                            ...getKarColor(parseFloat(editKarPct))
                          }}>%{editKarPct}</span>
                        ) : '-'
                      ) : f.transfer_edilir === false ? (
                        <span style={{ color: '#bbb' }}>–</span>
                      ) : (
                        f.dukkan_kar_pct != null ? (
                          <span style={{
                            display: 'inline-block', padding: '3px 10px', borderRadius: radius.lg, fontWeight: 'bold', fontSize: '11px',
                            background: karColor.bg, color: karColor.color
                          }}>%{f.dukkan_kar_pct}</span>
                        ) : <span style={{ color: '#ccc' }}>-</span>
                      )}
                    </td>
                    <td style={{ padding: '8px', textAlign: 'center' }}>
                      {isEditing ? (
                        editFoodCost ? (
                          <span style={{
                            display: 'inline-block', padding: '3px 10px', borderRadius: radius.lg, fontWeight: 'bold', fontSize: '11px',
                            ...getFoodCostColor(parseFloat(editFoodCost))
                          }}>%{editFoodCost}</span>
                        ) : '-'
                      ) : f.transfer_edilir === false ? (
                        <span style={{ color: '#bbb' }}>–</span>
                      ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
                          {f.food_cost_pct != null ? (
                            <span style={{
                              display: 'inline-block', padding: '3px 10px', borderRadius: radius.lg, fontWeight: 'bold', fontSize: '11px',
                              background: fcColor.bg, color: fcColor.color
                            }}>%{f.food_cost_pct}</span>
                          ) : <span style={{ color: '#ccc' }}>-</span>}
                          {posFoodCost != null && (
                            <span style={{
                              display: 'inline-block',
                              padding: '2px 8px',
                              borderRadius: radius.lg,
                              fontWeight: 'bold',
                              fontSize: '10px',
                              background: getFoodCostColor(parseFloat(posFoodCost)).bg,
                              color: getFoodCostColor(parseFloat(posFoodCost)).color
                            }}>
                              POS FC: %{posFoodCost.toFixed(1)}
                            </span>
                          )}
                        </div>
                      )}
                    </td>
                    <td style={{ padding: '8px', textAlign: 'center' }} onClick={e => e.stopPropagation()}>
                      {isEditing ? (
                        <div style={{ display: 'flex', gap: '6px', justifyContent: 'center' }}>
                          <button onClick={() => handleSave(f.malzeme_id)}
                            style={{
                              background: '#2e7d32', color: 'white', border: 'none', borderRadius: radius.sm,
                              padding: '6px 16px', cursor: 'pointer', fontSize: '12px', fontWeight: 'bold'
                            }}>
                            Kaydet
                          </button>
                          <button onClick={() => setEditingId(null)}
                            style={{
                              background: '#e0e0e0', color: '#333', border: 'none', borderRadius: radius.sm,
                              padding: '6px 12px', cursor: 'pointer', fontSize: '12px'
                            }}>
                            İptal
                          </button>
                        </div>
                      ) : canManageTransfer ? (
                        <button onClick={() => handleEdit(f)}
                          style={{
                            background: '#1565c0', color: 'white', border: 'none', borderRadius: radius.sm,
                            padding: '6px 16px', cursor: 'pointer', fontSize: '12px', fontWeight: 'bold'
                          }}>
                          Düzenle
                        </button>
                      ) : null}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        ) : (
          /* ===== TRANSFER GÖRÜNÜMÜ ===== */
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px', background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: radius.md }}>
            <thead>
              <tr style={{ background: colors.backgroundAlt, borderBottom: '2px solid #dee2e6' }}>
                <th style={{ padding: '7px', textAlign: 'left' }}>Ürün Kodu</th>
                <th style={{ padding: '7px', textAlign: 'left' }}>Ürün Adı</th>
                <th style={{ padding: '7px', textAlign: 'center', maxWidth: '100px' }}>Tip</th>
                <th style={{ padding: '7px', textAlign: 'right' }}>Üretim Maliyeti</th>
                <th style={{ padding: '7px', textAlign: 'right' }}>Kâr Marjı %</th>
                <th style={{ padding: '7px', textAlign: 'right' }}>Transfer Fiyatı</th>
                <th style={{ padding: '7px', textAlign: 'right' }}>Manuel Fiyat</th>
                <th style={{ padding: '7px', textAlign: 'right' }}>Efektif Fiyat</th>
                <th style={{ padding: '7px', textAlign: 'right' }}>Kâr Tutarı</th>
                <th style={{ padding: '7px', textAlign: 'center', width: '120px' }}>İşlem</th>
              </tr>
            </thead>
            <tbody>
              {fiyatlar.map(f => {
                const isEditing = editingId === f.malzeme_id;
                return (
                  <tr key={f.malzeme_id}
                      onClick={() => !isEditing && handleRowClick(f)}
                      style={{
                        borderBottom: `1px solid ${colors.border}`,
                        background: isEditing ? '#fff9c4' : 'transparent',
                        cursor: canManageTransfer && !isEditing ? 'pointer' : 'default',
                        transition: 'background 0.15s'
                      }}
                      onMouseEnter={e => { if (!isEditing && canManageTransfer) e.currentTarget.style.background = '#e3f2fd'; else if (!isEditing) e.currentTarget.style.background = '#f8f9fa'; }}
                      onMouseLeave={e => { if (!isEditing) e.currentTarget.style.background = 'transparent'; }}>
                    <td style={{ padding: '8px', fontFamily: 'monospace', fontWeight: 'bold', color: styles.primary }}>{f.malzeme_kod}</td>
                    <td style={{ padding: '8px' }}>{f.malzeme_ad}</td>
                    <td style={{ padding: '8px', textAlign: 'center', maxWidth: '100px' }}>
                      {renderTipBadge(f.tip)}
                      {renderYerindeBadge(f)}
                    </td>
                    <td style={{ padding: '8px', textAlign: 'right' }}>
                      <span style={{ fontFamily: 'monospace', color: '#888' }}>{(f.uretim_maliyeti || 0).toFixed(2)}</span>
                    </td>
                    <td style={editableCellStyle} onClick={e => { if (isEditing) e.stopPropagation(); }}>
                      {isEditing ? (
                        <input type="number" step="0.1" value={editForm.kar_marji_pct}
                          onChange={e => setEditForm(p => ({ ...p, kar_marji_pct: e.target.value }))}
                          style={{ ...inputStyle('#1565c0'), width: '70px' }} />
                      ) : (
                        <span style={editableValueStyle(true)}>%{(f.kar_marji_pct != null ? f.kar_marji_pct : 0).toFixed(1)}</span>
                      )}
                    </td>
                    <td style={{ padding: '8px', textAlign: 'right' }}>
                      {isEditing ? (
                        <span style={{ fontFamily: 'monospace', fontWeight: 'bold' }}>{previewFiyat}</span>
                      ) : f.transfer_edilir === false ? (
                        <span style={{ color: '#bbb' }}>–</span>
                      ) : (
                        <span style={{ fontFamily: 'monospace', fontWeight: 'bold' }}>{(f.transfer_fiyati != null ? f.transfer_fiyati : 0).toFixed(2)}</span>
                      )}
                    </td>
                    <td style={editableCellStyle} onClick={e => { if (isEditing) e.stopPropagation(); }}>
                      {isEditing ? (
                        <input type="number" step="0.01" value={editForm.manuel_fiyat}
                          onChange={e => setEditForm(p => ({ ...p, manuel_fiyat: e.target.value }))}
                          placeholder="-"
                          style={inputStyle('#e65100')} />
                      ) : (
                        <span style={editableValueStyle(f.manuel_fiyat)}>
                          {f.manuel_fiyat ? f.manuel_fiyat.toFixed(2) : '-'}
                        </span>
                      )}
                    </td>
                    <td style={{ padding: '8px', textAlign: 'right', fontWeight: 'bold' }}>
                      {f.transfer_edilir === false
                        ? <span style={{ color: '#bbb' }}>–</span>
                        : (f.efektif_fiyat != null
                          ? f.efektif_fiyat.toFixed(2)
                          : (f.transfer_fiyati != null ? f.transfer_fiyati.toFixed(2) : '-'))}
                    </td>
                    <td style={{ padding: '8px', textAlign: 'right', color: '#2e7d32', fontWeight: 'bold' }}>
                      {f.transfer_edilir === false
                        ? <span style={{ color: '#bbb' }}>–</span>
                        : (f.kar_tutari?.toFixed(2) || '-')}
                    </td>
                    <td style={{ padding: '8px', textAlign: 'center' }} onClick={e => e.stopPropagation()}>
                      {isEditing ? (
                        <div style={{ display: 'flex', gap: '6px', justifyContent: 'center' }}>
                          <button onClick={() => handleSave(f.malzeme_id)}
                            style={{
                              background: '#2e7d32', color: 'white', border: 'none', borderRadius: radius.sm,
                              padding: '6px 16px', cursor: 'pointer', fontSize: '12px', fontWeight: 'bold'
                            }}>
                            Kaydet
                          </button>
                          <button onClick={() => setEditingId(null)}
                            style={{
                              background: '#e0e0e0', color: '#333', border: 'none', borderRadius: radius.sm,
                              padding: '6px 12px', cursor: 'pointer', fontSize: '12px'
                            }}>
                            İptal
                          </button>
                        </div>
                      ) : canManageTransfer ? (
                        <button onClick={() => handleEdit(f)}
                          style={{
                            background: '#1565c0', color: 'white', border: 'none', borderRadius: radius.sm,
                            padding: '6px 16px', cursor: 'pointer', fontSize: '12px', fontWeight: 'bold'
                          }}>
                          Düzenle
                        </button>
                      ) : null}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}

        {/* Renk kodları */}
        {viewMode === 'karlilik' && fiyatlar.length > 0 && (
          <div style={{ display: 'flex', gap: '15px', marginTop: '12px', fontSize: '11px', color: colors.textSecondary, flexWrap: 'wrap' }}>
            <span><strong>Karlılık:</strong></span>
            <span style={{ padding: '2px 8px', borderRadius: radius.lg, background: '#f8d7da', color: '#721c24' }}>{'<=0% Zarar'}</span>
            <span style={{ padding: '2px 8px', borderRadius: radius.lg, background: '#fff3cd', color: '#856404' }}>{'<30% Düşük'}</span>
            <span style={{ padding: '2px 8px', borderRadius: radius.lg, background: '#d4edda', color: '#155724' }}>{'30-50% İyi'}</span>
            <span style={{ padding: '2px 8px', borderRadius: radius.lg, background: '#c8e6c9', color: '#1b5e20' }}>{'>50% Çok İyi'}</span>
            <span style={{ marginLeft: '15px' }}><strong>Food Cost:</strong></span>
            <span style={{ padding: '2px 8px', borderRadius: radius.lg, background: '#d4edda', color: '#155724' }}>{'<28% İdeal'}</span>
            <span style={{ padding: '2px 8px', borderRadius: radius.lg, background: '#fff3cd', color: '#856404' }}>{'28-35% Normal'}</span>
            <span style={{ padding: '2px 8px', borderRadius: radius.lg, background: '#ffe0b2', color: '#e65100' }}>{'35-50% Yüksek'}</span>
            <span style={{ padding: '2px 8px', borderRadius: radius.lg, background: '#f8d7da', color: '#721c24' }}>{'>50% Kritik'}</span>
          </div>
        )}
      </div>

      {/* ===== "+ Yeni Ürün Ekle" Modal ===== */}
      {showAddModal && (
        <div
          onClick={closeAddModal}
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 1000
          }}>
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: 'white', padding: '24px', borderRadius: radius.md,
              minWidth: '520px', maxWidth: '90vw', maxHeight: '90vh', overflowY: 'auto',
              boxShadow: shadows ? shadows.xl : '0 10px 30px rgba(0,0,0,0.2)'
            }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h2 style={{ margin: 0, fontSize: '18px', color: styles.primary }}>Transfer Fiyatlarına Yeni Ürün Ekle</h2>
              <button
                onClick={closeAddModal}
                style={{ background: 'transparent', border: 'none', fontSize: '20px', cursor: 'pointer', color: '#888' }}
                aria-label="Kapat">
                ×
              </button>
            </div>

            {/* Malzeme arama dropdown */}
            <div style={{ marginBottom: '14px' }}>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: 'bold', marginBottom: '6px', color: colors.textSecondary }}>
                Malzeme *
              </label>
              <input
                placeholder="Malzeme ara (kod veya ad)..."
                value={malzemeSearch}
                onChange={e => setMalzemeSearch(e.target.value)}
                style={{
                  width: '100%', padding: '8px 10px', border: `1px solid ${colors.border}`,
                  borderRadius: radius.sm, fontSize: '13px', boxSizing: 'border-box'
                }}
              />
              <div style={{
                marginTop: '6px', maxHeight: '220px', overflowY: 'auto',
                border: `1px solid ${colors.border}`, borderRadius: radius.sm, background: '#fafafa'
              }}>
                {filteredMalzemeler.length === 0 ? (
                  <div style={{ padding: '14px', textAlign: 'center', color: '#888', fontSize: '12px' }}>
                    {malzemeSearch ? 'Eşleşen malzeme yok' : 'Tüm aktif malzemeler zaten Transfer Fiyatları listesinde'}
                  </div>
                ) : (
                  filteredMalzemeler.slice(0, 50).map(m => {
                    const isSelected = selectedMalzemeId === m.id;
                    return (
                      <div
                        key={m.id}
                        onClick={() => handleSelectMalzeme(m)}
                        style={{
                          padding: '8px 10px',
                          background: isSelected ? '#e3f2fd' : 'transparent',
                          cursor: 'pointer',
                          borderBottom: `1px solid ${colors.border}`,
                          fontSize: '12px'
                        }}
                        onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = '#f0f4f8'; }}
                        onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = 'transparent'; }}>
                        <strong style={{ fontFamily: 'monospace', color: styles.primary }}>{m.kod}</strong>
                        {' '}- {m.ad}
                        {m.birim && <span style={{ color: '#888', marginLeft: '6px' }}>({m.birim})</span>}
                        {m.fiyat != null && (
                          <span style={{ float: 'right', color: '#2e7d32', fontFamily: 'monospace' }}>
                            {Number(m.fiyat).toFixed(2)} TL
                          </span>
                        )}
                      </div>
                    );
                  })
                )}
                {filteredMalzemeler.length > 50 && (
                  <div style={{ padding: '6px 10px', textAlign: 'center', color: '#888', fontSize: '11px', fontStyle: 'italic' }}>
                    +{filteredMalzemeler.length - 50} sonuç daha — aramayı daraltın
                  </div>
                )}
              </div>
            </div>

            {/* Üretim Maliyeti */}
            <div style={{ marginBottom: '14px' }}>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: 'bold', marginBottom: '6px', color: colors.textSecondary }}>
                Üretim Maliyeti (TL)
              </label>
              <input
                type="number"
                step="0.01"
                value={uretimMaliyeti}
                onChange={e => setUretimMaliyeti(e.target.value)}
                placeholder="0.00"
                style={{
                  width: '100%', padding: '8px 10px', border: `1px solid ${colors.border}`,
                  borderRadius: radius.sm, fontSize: '13px', boxSizing: 'border-box'
                }}
              />
              <div style={{ fontSize: '11px', color: colors.textTertiary, marginTop: '4px' }}>
                Boş bırakırsanız malzemenin son alış fiyatı kullanılır.
              </div>
            </div>

            {/* Kâr Marjı % */}
            <div style={{ marginBottom: '14px' }}>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: 'bold', marginBottom: '6px', color: colors.textSecondary }}>
                Kâr Marjı %
              </label>
              <input
                type="number"
                step="0.5"
                value={karMarjiPct}
                onChange={e => setKarMarjiPct(e.target.value)}
                placeholder={String(defaultMargin)}
                style={{
                  width: '100%', padding: '8px 10px', border: `1px solid ${colors.border}`,
                  borderRadius: radius.sm, fontSize: '13px', boxSizing: 'border-box'
                }}
              />
              <div style={{ fontSize: '11px', color: colors.textTertiary, marginTop: '4px' }}>
                Varsayılan: %{defaultMargin}, ürüne göre değiştirebilirsiniz.
              </div>
            </div>

            {/* Manuel Fiyat (opsiyonel) */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: 'bold', marginBottom: '6px', color: colors.textSecondary }}>
                Manuel Fiyat (opsiyonel)
              </label>
              <input
                type="number"
                step="0.01"
                value={manuelFiyat}
                onChange={e => setManuelFiyat(e.target.value)}
                placeholder="-"
                style={{
                  width: '100%', padding: '8px 10px', border: `1px solid ${colors.border}`,
                  borderRadius: radius.sm, fontSize: '13px', boxSizing: 'border-box'
                }}
              />
              <div style={{ fontSize: '11px', color: colors.textTertiary, marginTop: '4px' }}>
                Doluysa kâr marjı yerine bu fiyat kullanılır.
              </div>
            </div>

            {/* Buttonlar */}
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button
                onClick={closeAddModal}
                disabled={addLoading}
                style={{
                  background: '#e0e0e0', color: '#333', border: 'none', borderRadius: radius.sm,
                  padding: '9px 18px', cursor: addLoading ? 'wait' : 'pointer', fontSize: '13px'
                }}>
                İptal
              </button>
              <button
                onClick={handleAddProduct}
                disabled={addLoading || !selectedMalzemeId}
                style={{
                  background: '#2e7d32', color: 'white', border: 'none', borderRadius: radius.sm,
                  padding: '9px 22px', cursor: (addLoading || !selectedMalzemeId) ? 'not-allowed' : 'pointer',
                  fontSize: '13px', fontWeight: 'bold',
                  opacity: (addLoading || !selectedMalzemeId) ? 0.6 : 1
                }}>
                {addLoading ? 'Ekleniyor...' : 'Ekle'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TransferFiyatPage;
